import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Key, Plus, Trash2, Copy, AlertTriangle, Check, X } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './ApiKeys.css';

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const { showSuccess, showError } = useToast();

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/api-keys');
      if (res.data.success) setKeys(res.data.keys || []);
    } catch (err) {
      showError('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      const res = await api.post('/api/api-keys', { name: newKeyName.trim() });
      if (res.data.success) {
        setFreshKey(res.data.key);
        setNewKeyName('');
        setShowCreate(false);
        await fetchKeys();
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;

    try {
      const res = await api.delete(`/api/api-keys/${id}`);
      if (res.data.success) {
        showSuccess('API key revoked');
        await fetchKeys();
      }
    } catch (err) {
      showError('Failed to revoke key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-keys-page">
      <div className="api-keys-header">
        <div>
          <h1 className="api-keys-title">API Keys</h1>
          <p className="api-keys-subtitle">
            Keys for external apps to access CXM data (e.g., NPS scores).
          </p>
        </div>
        <motion.button
          className="create-key-btn"
          onClick={() => setShowCreate(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Create Key
        </motion.button>
      </div>

      {/* Fresh key banner — shown once after creation */}
      {freshKey && (
        <motion.div
          className="fresh-key-banner"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="fresh-key-warning">
            <AlertTriangle size={20} />
            <div>
              <strong>Copy this key now.</strong> It will not be shown again.
            </div>
          </div>
          <div className="fresh-key-value">
            <code>{freshKey}</code>
            <button onClick={() => copyToClipboard(freshKey)} className="copy-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button className="dismiss-btn" onClick={() => setFreshKey(null)}>
            <X size={16} /> Dismiss
          </button>
        </motion.div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <motion.div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="modal-header">
              <h3>Create API Key</h3>
              <button className="close-btn" onClick={() => setShowCreate(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="modal-body">
              <label>
                Name <span className="required">*</span>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., HR App Integration"
                  autoFocus
                  required
                />
              </label>
              <p className="help-text">
                Give it a descriptive name so you can identify it later.
              </p>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="btn-primary">
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="loading">
          <div className="loading-spinner" />
          <p>Loading API keys...</p>
        </div>
      ) : keys.length === 0 ? (
        <div className="empty-state">
          <Key size={48} />
          <p>No API keys yet. Create one to allow external apps to access your data.</p>
        </div>
      ) : (
        <div className="keys-table-container">
          <table className="keys-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <motion.tr
                  key={k.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <td className="name-cell">{k.name}</td>
                  <td><code className="key-prefix">{k.key_prefix}...</code></td>
                  <td>{new Date(k.created_at).toLocaleDateString()}</td>
                  <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</td>
                  <td>
                    <span className={`status-badge ${k.is_active ? 'active' : 'inactive'}`}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(k.id, k.name)}
                      title="Revoke"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
